/**
 * React Query hooks для рабочего места руководителя (задача 71 §5.1/§5.2).
 *
 * `useWeekSummary` — недельная сводка для полосы-чипов (§5.1).
 * `useDayFeed` — лента сданных отчётов выбранного дня (§5.2).
 * `useMissing` — несдавшие за день для плашки (§5.3).
 * `useApproveTimesheet` / `useUnapproveTimesheet` — по-отчётное подтверждение и
 * отмена (§1.1) с оптимистичным апдейтом обоих кэшей: карточка ленты меняет
 * статус, разрез `▣` чипа полосы падает при подтверждении и растёт при отмене.
 *
 * `unitId` null = лид с единственным прямым юнитом (бэк резолвит сам);
 * admin обязан передать unit_id (иначе 422). 403/401 не ретраим.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError } from "@/api/client";
import {
  approveTimesheet,
  approveWeek,
  approveWeekBulk,
  getDayFeed,
  getMissing,
  getWeekFeed,
  getWeekSummary,
  unapproveTimesheet,
  type DayReportFeed,
  type MissingList,
  type TimesheetApprovalState,
  type WeekApproveOutcome,
  type WeekBulkApproveResult,
  type WeekFeed,
  type WeekSummary,
} from "@/api/approval";

export const WEEK_SUMMARY_KEY = (
  weekStart: string,
  unitId: number | null,
  includeChildren: boolean,
) => ["approval-week", weekStart, unitId, includeChildren] as const;

export const DAY_FEED_KEY = (date: string, unitId: number | null, includeChildren: boolean) =>
  ["approval-day", date, unitId, includeChildren] as const;

export const MISSING_KEY = (date: string, unitId: number | null, includeChildren: boolean) =>
  ["approval-missing", date, unitId, includeChildren] as const;

export const WEEK_FEED_KEY = (weekStart: string, unitId: number | null, includeChildren: boolean) =>
  ["approval-week-feed", weekStart, unitId, includeChildren] as const;

/** Все read-model семейства, которые меняет статус или содержимое отчёта. */
export function invalidateApprovalQueries(
  qc: ReturnType<typeof useQueryClient>,
  options: { includeAdminTimesheet?: boolean } = {},
): void {
  void qc.invalidateQueries({ queryKey: ["approval-day"] });
  void qc.invalidateQueries({ queryKey: ["approval-missing"] });
  void qc.invalidateQueries({ queryKey: ["approval-week"] });
  void qc.invalidateQueries({ queryKey: ["approval-week-feed"] });
  if (options.includeAdminTimesheet !== false) {
    void qc.invalidateQueries({ queryKey: ["admin-timesheet"] });
  }
}

function noRetryOnAuth(failureCount: number, error: Error): boolean {
  if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
    return false;
  }
  return failureCount < 1;
}

export function useWeekSummary(
  weekStart: string,
  unitId: number | null = null,
  includeChildren = false,
  enabled = true,
) {
  return useQuery<WeekSummary, Error>({
    queryKey: WEEK_SUMMARY_KEY(weekStart, unitId, includeChildren),
    queryFn: () => getWeekSummary(weekStart, unitId, includeChildren),
    enabled,
    retry: noRetryOnAuth,
  });
}

export function useDayFeed(
  date: string,
  unitId: number | null = null,
  includeChildren = false,
  enabled = true,
) {
  return useQuery<DayReportFeed, Error>({
    queryKey: DAY_FEED_KEY(date, unitId, includeChildren),
    queryFn: () => getDayFeed(date, unitId, includeChildren),
    enabled,
    retry: noRetryOnAuth,
  });
}

export function useMissing(
  date: string,
  unitId: number | null = null,
  includeChildren = false,
  enabled = true,
) {
  return useQuery<MissingList, Error>({
    queryKey: MISSING_KEY(date, unitId, includeChildren),
    queryFn: () => getMissing(date, unitId, includeChildren),
    enabled,
    retry: noRetryOnAuth,
  });
}

interface ApprovalVars {
  userId: number;
  date: string;
  /** ISO-понедельник недели карточки — для точечного апдейта week-summary кэша. */
  weekStart: string;
  unitId: number | null;
  includeChildren?: boolean;
  /**
   * Combined-финал «табель + approve» (задача 79 §1.1): табель в полу-часах
   * выставляется перед approve. `undefined` — прежнее по-отчётное подтверждение
   * без правки табеля (карточка ленты «Подтвердить»). Игнорируется на unapprove.
   */
  declaredHalf?: number | null;
}

/** Оптимистично переключить статус карточки субъекта в ленте дня. */
function patchDayFeedStatus(
  qc: ReturnType<typeof useQueryClient>,
  vars: ApprovalVars,
  status: "approved" | "signed",
): DayReportFeed | undefined {
  const key = DAY_FEED_KEY(vars.date, vars.unitId, vars.includeChildren ?? false);
  const prev = qc.getQueryData<DayReportFeed>(key);
  if (!prev) return undefined;
  qc.setQueryData<DayReportFeed>(key, {
    ...prev,
    cards: prev.cards.map((c) => (c.user.id === vars.userId ? { ...c, status } : c)),
  });
  return prev;
}

/** Оптимистично сдвинуть разрезы чипа дня в недельной сводке (±1 ▣ / ✓). */
function patchWeekChip(
  qc: ReturnType<typeof useQueryClient>,
  vars: ApprovalVars,
  delta: 1 | -1,
): WeekSummary | undefined {
  const key = WEEK_SUMMARY_KEY(vars.weekStart, vars.unitId, vars.includeChildren ?? false);
  const prev = qc.getQueryData<WeekSummary>(key);
  if (!prev) return undefined;
  qc.setQueryData<WeekSummary>(key, {
    ...prev,
    days: prev.days.map((d) => {
      if (d.date !== vars.date) return d;
      // delta=-1 → подтверждение (▣→✓): pending−1, approved+1.
      // delta=+1 → отмена (✓→▣): pending+1, approved−1.
      const pending = Math.max(0, d.pending_count + delta);
      const approved = Math.max(0, d.approved_count - delta);
      const { missing_count: missing, expected_count: expected } = d;
      const state =
        expected === 0 && pending === 0 && approved === 0
          ? "no_data"
          : pending > 0 && missing > 0
            ? "both"
            : pending > 0
              ? "pending"
              : missing > 0
                ? "missing"
                : "all_clear";
      return { ...d, pending_count: pending, approved_count: approved, state };
    }),
  });
  return prev;
}

function useApprovalMutation(action: "approve" | "unapprove") {
  const qc = useQueryClient();
  return useMutation<
    TimesheetApprovalState,
    Error,
    ApprovalVars,
    {
      prevDay?: DayReportFeed;
      prevWeek?: WeekSummary;
    }
  >({
    mutationFn: (vars) =>
      action === "approve"
        ? approveTimesheet(vars.userId, vars.date, vars.declaredHalf)
        : unapproveTimesheet(vars.userId, vars.date),
    onMutate: async (vars) => {
      await Promise.all([
        qc.cancelQueries({
          queryKey: DAY_FEED_KEY(vars.date, vars.unitId, vars.includeChildren ?? false),
        }),
        qc.cancelQueries({
          queryKey: WEEK_SUMMARY_KEY(vars.weekStart, vars.unitId, vars.includeChildren ?? false),
        }),
      ]);
      const prevDay = patchDayFeedStatus(qc, vars, action === "approve" ? "approved" : "signed");
      const prevWeek = patchWeekChip(qc, vars, action === "approve" ? -1 : 1);
      return { prevDay, prevWeek };
    },
    onError: (_err, vars, ctx) => {
      if (ctx?.prevDay) {
        qc.setQueryData(
          DAY_FEED_KEY(vars.date, vars.unitId, vars.includeChildren ?? false),
          ctx.prevDay,
        );
      }
      if (ctx?.prevWeek)
        qc.setQueryData(
          WEEK_SUMMARY_KEY(vars.weekStart, vars.unitId, vars.includeChildren ?? false),
          ctx.prevWeek,
        );
    },
    onSettled: (_data, _error, vars) => {
      // Combined-финал закрывает редактор. Его privileged GET сам снимает
      // approval, поэтому refetch активного snapshot отменил бы approve.
      const combinedFinalize = action === "approve" && vars.declaredHalf !== undefined;
      invalidateApprovalQueries(qc, { includeAdminTimesheet: !combinedFinalize });
    },
  });
}

export function useApproveTimesheet() {
  return useApprovalMutation("approve");
}

export function useUnapproveTimesheet() {
  return useApprovalMutation("unapprove");
}

// ── Недельная лента (задача 116 §2.2) ──────────────────────────────────

/** Недельная лента weekly-юнитов сцены (§1.2). */
export function useWeekFeed(
  weekStart: string,
  unitId: number | null = null,
  includeChildren = false,
  enabled = true,
) {
  return useQuery<WeekFeed, Error>({
    queryKey: WEEK_FEED_KEY(weekStart, unitId, includeChildren),
    queryFn: () => getWeekFeed(weekStart, unitId, includeChildren),
    enabled,
    retry: noRetryOnAuth,
  });
}

interface ApproveWeekVars {
  userId: number;
  weekStart: string;
  unitId: number | null;
  includeChildren?: boolean;
}

/** Оптимистично переключить производное состояние строки в ленте недели. */
function patchWeekRowConfirmationState(
  qc: ReturnType<typeof useQueryClient>,
  vars: ApproveWeekVars,
  confirmationState: "pending" | "confirmed",
): WeekFeed | undefined {
  const key = WEEK_FEED_KEY(vars.weekStart, vars.unitId, vars.includeChildren ?? false);
  const prev = qc.getQueryData<WeekFeed>(key);
  if (!prev) return undefined;
  qc.setQueryData<WeekFeed>(key, {
    ...prev,
    groups: prev.groups.map((g) => ({
      ...g,
      rows: g.rows.map((r) =>
        r.user.id === vars.userId
          ? {
              ...r,
              confirmation_state: confirmationState,
              is_week_confirmed: confirmationState === "confirmed",
            }
          : r,
      ),
    })),
  });
  return prev;
}

/**
 * Подтвердить неделю человека (§1.3): все `signed`-отчёты → `approved` одним
 * действием. Оптимистично двигает прогресс «Подтверждено N / M», откат при ошибке
 * (напр. 422 на гонке с опустевшим днём), инвалидация ленты `onSettled`.
 */
export function useApproveWeek() {
  const qc = useQueryClient();
  return useMutation<WeekApproveOutcome, Error, ApproveWeekVars, { prev?: WeekFeed }>({
    mutationFn: (vars) => approveWeek(vars.userId, vars.weekStart),
    onMutate: async (vars) => {
      await qc.cancelQueries({
        queryKey: WEEK_FEED_KEY(vars.weekStart, vars.unitId, vars.includeChildren ?? false),
      });
      return { prev: patchWeekRowConfirmationState(qc, vars, "confirmed") };
    },
    onError: (_err, vars, ctx) => {
      if (ctx?.prev)
        qc.setQueryData(
          WEEK_FEED_KEY(vars.weekStart, vars.unitId, vars.includeChildren ?? false),
          ctx.prev,
        );
    },
    onSettled: () => {
      invalidateApprovalQueries(qc);
    },
  });
}

interface ApproveWeekBulkVars {
  weekStart: string;
  unitId: number;
  includeChildren: boolean;
}

/** «Подтвердить всех» (§1.3): подтверждает «чистых», возвращает итог для попапа 3b. */
export function useApproveWeekBulk() {
  const qc = useQueryClient();
  return useMutation<WeekBulkApproveResult, Error, ApproveWeekBulkVars>({
    mutationFn: (vars) => approveWeekBulk(vars.weekStart, vars.unitId, vars.includeChildren),
    onSettled: (_data, _err, vars) => {
      void qc.invalidateQueries({
        queryKey: WEEK_FEED_KEY(vars.weekStart, vars.unitId, vars.includeChildren),
      });
    },
  });
}

interface UnapproveWeekVars {
  userId: number;
  weekStart: string;
  unitId: number | null;
  includeChildren?: boolean;
  /** Даты недели со статусом `approved` — их дневной unapprove откатывает неделю. */
  approvedDates: string[];
}

/**
 * Отменить подтверждение недели (§1 решений — unapprove остаётся дневным): дневной
 * `unapprove` по каждому подтверждённому дню, композицией, без нового эндпоинта.
 * Оптимистично снимает пометку «подтверждена» с карточки.
 */
export function useUnapproveWeek() {
  const qc = useQueryClient();
  return useMutation<void, Error, UnapproveWeekVars, { prev?: WeekFeed }>({
    mutationFn: async (vars) => {
      for (const date of vars.approvedDates) {
        await unapproveTimesheet(vars.userId, date);
      }
    },
    onMutate: async (vars) => {
      await qc.cancelQueries({
        queryKey: WEEK_FEED_KEY(vars.weekStart, vars.unitId, vars.includeChildren ?? false),
      });
      return { prev: patchWeekRowConfirmationState(qc, vars, "pending") };
    },
    onError: (_err, vars, ctx) => {
      if (ctx?.prev)
        qc.setQueryData(
          WEEK_FEED_KEY(vars.weekStart, vars.unitId, vars.includeChildren ?? false),
          ctx.prev,
        );
    },
    onSettled: () => {
      invalidateApprovalQueries(qc);
    },
  });
}
